import { SELECTED_PRODUCTS_KEY } from "@constants/storageKeys"
import useElevarEvents, { ElevarProductProps } from "@hooks/useElevarEvents"
import useEnhancedEcommerceEvents from "@hooks/useEnhancedEcommerceEvents"
import { useAllMarketPricing } from "@hooks/useMarketPricing"
import { Dispatch, RootState } from "@redux/store"
import { getIn } from "@utils/index"
import { getMarketAmount } from "@utils/pricing"
import { getLocalStorage, writeLocalStorage } from "@utils/storage"
import { getShopURLForBrandDeviceType, getShopURLForDevice } from "@utils/urls"
import { graphql, useStaticQuery } from "gatsby"
import _ from "lodash-es"
import { matchSorter } from "match-sorter"
import * as R from "ramda"
import React from "react"
import { connect } from "react-redux"
import { compose } from "recompose"

import StaticQueryErrorBoundary from "../../StaticQueryErrorBoundary"
import Search from "../index"

const ESCAPE_KEY = "Escape"
const ENTER_KEY = "Enter"

const searchKeys = [
  { key: "productName", threshold: matchSorter.rankings.MATCHES },
  { key: "variants.*.sku", threshold: matchSorter.rankings.CONTAINS },
  { key: "variants.*.upc", threshold: matchSorter.rankings.CONTAINS },
]

const formatLink = ({ name, slug }) => {
  return {
    text: name,
    to: slug,
  }
}

const withMatchSorter: any = (WrappedComponent: any) => (props: any) => {
  const data = useStaticQuery(graphql`
    query SearchDataFields {
      allContentfulProduct(
        filter: {
          internalTitle: {
            nin: [
              "GraphQL schema - do not delete"
              "GraphQL schema - do not delete - Upsell"
            ]
          }
        }
      ) {
        nodes {
          slug
          productName
          brand {
            slug
            name
          }
          device {
            slug
            name
            type {
              slug
              name
            }
          }
          series {
            tags {
              text
            }
          }
          variants {
            shopifyVariantId
            id
            sku
            upc
            price
            compareAtPrice
            name
            images {
              file {
                url
              }
            }
          }
          shopifyProduct {
            shopifyProductId
          }
        }
      }
    }
  `)

  // Fetch market pricing data at runtime
  const { data: marketPricingData, loading: marketPricingLoading } =
    useAllMarketPricing()

  // Create a Map for quick SKU lookups
  const marketPricingMap = React.useMemo(() => {
    const map = new Map()
    marketPricingData.forEach((variant) => {
      map.set(variant.sku, variant.marketPrices)
    })
    return map
  }, [marketPricingData])

  // Merge market pricing data with product data
  const productData = React.useMemo(() => {
    const nodes = getIn(data, "allContentfulProduct.nodes", [])

    return nodes
      .filter(({ variants }) => variants && variants.length > 0)
      .map((product) => ({
        ...product,
        variants: product.variants.map((variant) => ({
          ...variant,
          marketPrices: marketPricingMap.get(variant.sku) || {},
        })),
      }))
  }, [data, marketPricingMap])

  const [searchResults, setSearchResults] = React.useState([])
  const [searchCategories, setSearchCategories] = React.useState([])
  const [isPristine, setIsPristine] = React.useState(true)

  const onKeyDown = (event: any) => {
    if (event.key === ESCAPE_KEY) {
      props.onRequestClose()
    }
    if (event.key === ENTER_KEY && event.currentTarget) {
      event.currentTarget.blur()
    }
  }

  const onChange = (event) => {
    props.setSearchValue(getIn(event, "target.value", ""))
  }

  const onClickSuggestion = (suggestion) => {
    if (suggestion) {
      props.setSearchValue(suggestion)
    }
  }

  const clearData = () => {
    props.setSearchValue("")
    setSearchResults([])
    setSearchCategories([])
  }

  const { onClickProductCard } = useEnhancedEcommerceEvents()
  const { elevarOnSelectItem } = useElevarEvents()

  const formatResults = (items) => {
    return items.reduce(
      (resultData: any, item: any, currentIndex) => {
        const shopifyProductId = item?.shopifyProduct?.shopifyProductId
        const tags = item?.series?.tags
        const slug = item?.slug
        const name = item?.productName
        const imageUrl = getIn(item, "variants.0.images.0.file.url", undefined)

        const rawDevice = item.device || {}
        const device = {
          name: rawDevice.name || "",
          slug: getShopURLForDevice(rawDevice.slug || ""),
        }
        const rawDeviceType = rawDevice.type || {}
        const deviceType = {
          name: rawDeviceType.name || "",
          slug: rawDeviceType.slug || "",
        }
        const rawBrand = item.brand || {}
        const brand = {
          name: rawBrand.name || "",
          slug: rawBrand.slug || "",
        }
        const brandDeviceType = {
          name: `${brand.name} ${deviceType.name}`,
          slug: getShopURLForBrandDeviceType(brand.slug, deviceType.slug),
        }

        const variant = getIn(item, "variants.0")
        const price =
          getMarketAmount(variant, props.customerCountryCode, "price") || ""
        const marketPrices = variant.marketPrices || {}

        const gtmProduct = {
          brand: rawBrand.name || "",
          device: rawDevice.name || "",
          deviceType: rawDeviceType.name || "",
          list: `Search Results: ${props.searchValue}`,
          price: price,
          productId: shopifyProductId,
          productName: name,
          sku: variant.sku,
          variantName: variant.name || "",
        }

        const elevarProduct: ElevarProductProps = {
          brand: "Urban Armor Gear",
          device: rawDevice.name || "",
          deviceType: rawDeviceType.name || "",
          image: imageUrl,
          list: "search results",
          position: currentIndex + 1,
          price: price,
          productId: shopifyProductId,
          productName: name,
          sku: variant.sku || "",
          variantId: variant.shopifyVariantId || "",
          variantName: variant.name || "",
        }

        let discountTag
        let encodedProductId

        try {
          encodedProductId = atob(shopifyProductId)
        } catch {
          encodedProductId = shopifyProductId
        }

        props.automaticDiscounts.forEach((automaticDiscount) => {
          const discountId = automaticDiscount?.id
          const parsedDiscountId = discountId.replace(
            "gid://shopify/DiscountAutomaticNode/",
            ""
          )
          const discountConfig = props.automaticDiscountData?.find(
            ({ discountId }) => discountId === parsedDiscountId
          )

          if (
            discountConfig &&
            automaticDiscount?.customerBuysProducts?.indexOf(encodedProductId) >
              -1
          ) {
            discountTag = {
              backgroundColor: discountConfig.tagX?.backgroundColor,
              color: discountConfig.tagX?.color,
              mobileText: discountConfig.tagX?.mobileText,
              text: discountConfig.tagX?.text,
            }
          } else if (
            discountConfig &&
            automaticDiscount?.customerGetsProducts?.indexOf(encodedProductId) >
              -1
          ) {
            discountTag = {
              backgroundColor: discountConfig.tagY?.backgroundColor,
              color: discountConfig.tagY?.color,
              mobileText: discountConfig.tagY?.mobileText,
              text: discountConfig.tagY?.text,
            }
          }
        })

        return {
          brandDeviceTypes: [...resultData.brandDeviceTypes, brandDeviceType],
          devices: [...resultData.devices, device],
          products: [
            ...resultData.products,
            {
              elevarProduct,
              gtmProduct,
              imageUrl,
              marketPrices,
              name,
              onClick: () => {
                const selectedProducts =
                  getLocalStorage(SELECTED_PRODUCTS_KEY) || {}
                writeLocalStorage(SELECTED_PRODUCTS_KEY, {
                  ...selectedProducts,
                  [elevarProduct.productId]: "search results",
                })

                onClickProductCard(gtmProduct)
                elevarOnSelectItem(elevarProduct, "search results")
              },
              price: `$${price}`,
              sku: variant.sku,
              slug,
              tags: discountTag ? [discountTag, ...(tags || [])] : tags,
            },
          ],
        }
      },
      { brandDeviceTypes: [], devices: [], products: [] }
    )
  }

  const formatSearchCategories = (items) => {
    return R.pipe(
      R.uniq,
      // @ts-expect-error Ignore
      R.reject(({ name, slug }) => !slug || !name),
      R.map((category: any) => formatLink(category)),
      R.uniqBy(({ text }) => R.toLower(text))
    )(items)
  }

  const filterProducts = () => {
    if (isPristine) {
      setIsPristine(false)
    }

    if (props.searchValue) {
      const products: any = matchSorter(productData, props.searchValue.trim(), {
        keys: searchKeys,
      })

      if (products.length > 0) {
        const formattedResults = formatResults(products)

        setSearchResults(formattedResults.products) as any
        setSearchCategories(
          formatSearchCategories([
            ...R.take(16, formattedResults.devices),
            ...R.take(4, formattedResults.brandDeviceTypes),
          ]) as any
        )
      } else {
        setSearchResults([])
        setSearchCategories([])
      }
    }
  }

  const handleFilterProductsDebounced = _.debounce(filterProducts, 100, {
    leading: true,
    maxWait: 250,
    trailing: true,
  })

  React.useEffect(() => {
    handleFilterProductsDebounced()
  }, [props.searchValue, props.automaticDiscounts])

  return (
    <WrappedComponent
      isLoadingPricing={marketPricingLoading}
      isPristine={isPristine}
      lastSearchResult={props.lastSearchResult}
      onChange={onChange}
      onClickSuggestion={onClickSuggestion}
      onKeyDown={onKeyDown}
      onRequestClear={clearData}
      searchCategories={searchCategories}
      searchResults={searchResults}
      setLastSearchResult={props.setLastSearchResult}
      {...props}
    />
  )
}

const mapState = (state: RootState) => ({
  automaticDiscounts: state.discounts.automaticDiscounts,
  customerCountryCode: state.customer.customerLocation.customerCountryCode,
  lastSearchResult: state.search.lastSearchResult,
  searchValue: state.search.searchValue,
})

const mapDispatch = (dispatch: Dispatch) => ({
  setLastSearchResult: dispatch.search.setLastSearchResult,
  setSearchValue: dispatch.search.setSearchValue,
})

const ConnectedSearch = compose(
  connect(mapState, mapDispatch),
  withMatchSorter
)(Search as any) as any

// Wrap with error boundary to handle static query failures (UAG-WEB-M)
export default function SearchWithErrorBoundary(props: any) {
  return (
    <StaticQueryErrorBoundary>
      <ConnectedSearch {...props} />
    </StaticQueryErrorBoundary>
  )
}
